import { useState } from 'react';
import { cn } from '@/lib/cn';
import { PRESENTING_SYMPTOMS_KEY, formatAnswerValue, readSnapshot, type SymptomAnswer } from './symptomForm';

/** Longer than this and free text is body copy, not a headline. */
const HEADLINE_MAX = 60;

/**
 * The symptoms half of Consultation's "Why today" card. Structured visits
 * show the presenting symptoms as chips and every other answer as a compact
 * label/value row; a visit recorded before the form existed shows its free
 * text, as a headline only when it is short. Long text is clamped to three
 * lines with "Show more", so a pasted paragraph never takes over the rail.
 */
export function WhyTodaySymptoms({ vitals, fallback }: { vitals: Record<string, unknown> | null | undefined; fallback: string }) {
  const snapshot = readSnapshot(vitals);
  if (snapshot) {
    const presenting = snapshot.answers.find((a) => a.key === PRESENTING_SYMPTOMS_KEY && Array.isArray(a.value));
    const details = snapshot.answers.filter((a) => a !== presenting);
    return (
      <div>
        {presenting ? (
          <div className="flex flex-wrap gap-1.5" aria-label="Presenting symptoms">
            {(presenting.value as string[]).map((s) => (
              <span key={s} className="rounded-full bg-accent px-2.5 py-0.5 text-[13.5px] font-semibold text-teal">{s}</span>
            ))}
          </div>
        ) : !details.length ? (
          <div className="text-[19px] font-semibold leading-tight text-ink">{fallback}</div>
        ) : null}
        {details.length > 0 && (
          <dl className={cn('m-0 grid grid-cols-[auto_1fr] gap-x-2.5 gap-y-1 text-[12.5px]', presenting && 'mt-2')}>
            {details.map((a) => <DetailRow key={a.key} answer={a} />)}
          </dl>
        )}
      </div>
    );
  }

  const text = typeof vitals?.symptoms === 'string' ? vitals.symptoms.trim() : '';
  if (!text) return <div className="text-[19px] font-semibold leading-tight text-ink">{fallback}</div>;
  if (text.length <= HEADLINE_MAX) return <div className="text-[19px] font-semibold leading-tight text-ink">{text}</div>;
  return <Clamped text={text} className="text-[13px] leading-snug text-ink" />;
}

function DetailRow({ answer }: { answer: SymptomAnswer }) {
  const value = formatAnswerValue(answer);
  return (
    <>
      <dt className="text-ink-muted">{answer.label}</dt>
      <dd className="m-0 min-w-0 font-medium text-ink">
        {value.length > 120 ? <Clamped text={value} /> : value}
      </dd>
    </>
  );
}

function Clamped({ text, className }: { text: string; className?: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={className}>
      <div className={cn('whitespace-pre-line break-words', !open && 'line-clamp-3')}>{text}</div>
      <button type="button" onClick={() => setOpen((o) => !o)}
        className="mt-0.5 bg-transparent p-0 text-[11px] text-teal underline-offset-2 hover:underline">
        {open ? 'Show less' : 'Show more'}
      </button>
    </div>
  );
}
